import React, { useEffect, useRef, useState } from 'react'
import HomeHeader from '../components/HomePageHeader'
import Footer from '../components/Footer'
import GameRow from './components/GameRow'
import GameCard from './components/GameCard'
import GameModal from './components/GameModal'
import { getGames } from '../services/games'
import { GAME_CATEGORIES } from '../constants'
import { slugifyCategory } from './categorySlugs'
import styles from './Games.module.css'

const ROW_LIMIT = 20
const SEARCH_LIMIT = 40
const SEARCH_DEBOUNCE_MS = 300

// Every row on this page is a slice of the same "newest first" ordering, so the whole directory
// is built from a single `GET /v3/showcase` call rather than one call per row (was: 1 + 1 +
// GAME_CATEGORIES.length separate requests, each triggering its own backend query). This assumes
// the curated set comfortably fits in one page - if the catalog outgrows that, this should become
// a dedicated "directory" endpoint that returns pre-grouped sections instead of raising the limit
// further.
const DIRECTORY_FETCH_LIMIT = 200

const buildSections = (games) => [
  { title: 'Featured games', linkTo: 'featured', games: games.filter((g) => g.featured).slice(0, ROW_LIMIT) },
  { title: 'New games', linkTo: 'new-games', games: games.slice(0, ROW_LIMIT) },
  ...GAME_CATEGORIES.map((category) => ({
    title: category,
    linkTo: slugifyCategory(category),
    games: games.filter((g) => g.category === category).slice(0, ROW_LIMIT)
  }))
]

const GamesDirectory = () => {
  const [sections, setSections] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [activeSubdomain, setActiveSubdomain] = useState(null)

  const [query, setQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searchCursor, setSearchCursor] = useState(undefined)
  const [searching, setSearching] = useState(false)
  const [searchLoadingMore, setSearchLoadingMore] = useState(false)
  const [searchError, setSearchError] = useState(false)

  // Bumped every time `query` changes, so a "Load more" request kicked off for an older query
  // (still in flight when the user types a new one) can tell it's been superseded and drop its
  // response instead of appending stale games/cursor onto the newer search's results.
  const searchRequestIdRef = useRef(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadError(false)

    getGames({ sort: 'new', limit: DIRECTORY_FETCH_LIMIT })
      .then((data) => {
        if (cancelled) return
        setSections(buildSections(data?.games || []).filter((row) => row.games.length))
      })
      .catch(() => {
        if (!cancelled) setLoadError(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    searchRequestIdRef.current += 1
    const requestId = searchRequestIdRef.current

    if (!query.trim()) {
      setSearchResults([])
      setSearchCursor(undefined)
      setSearchError(false)
      return
    }

    setSearching(true)
    setSearchError(false)

    const timer = setTimeout(() => {
      getGames({ search: query.trim(), limit: SEARCH_LIMIT })
        .then((data) => {
          if (searchRequestIdRef.current !== requestId) return
          setSearchResults(data?.games || [])
          setSearchCursor(data?.nextCursor || undefined)
        })
        .catch(() => {
          if (searchRequestIdRef.current === requestId) setSearchError(true)
        })
        .finally(() => {
          if (searchRequestIdRef.current === requestId) setSearching(false)
        })
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      clearTimeout(timer)
    }
  }, [query])

  const loadMoreSearchResults = async () => {
    const requestId = searchRequestIdRef.current
    setSearchLoadingMore(true)
    try {
      const data = await getGames({ search: query.trim(), limit: SEARCH_LIMIT, cursor: searchCursor })
      if (searchRequestIdRef.current !== requestId) return
      setSearchResults((prev) => [...prev, ...(data?.games || [])])
      setSearchCursor(data?.nextCursor || undefined)
    } catch {
      if (searchRequestIdRef.current === requestId) setSearchError(true)
    } finally {
      if (searchRequestIdRef.current === requestId) setSearchLoadingMore(false)
    }
  }

  const isSearching = !!query.trim()

  return (
    <div className={`full-height ${styles.page}`}>
      <HomeHeader />

      <div className={styles.hero}>
        <h1 className={styles.heroTitle}>Play games hosted on Tiiny</h1>
        <p className={styles.heroSubtitle}>A curated showcase of games built and hosted by the Tiiny community.</p>

        <div className={styles.searchBar}>
          <i className="bi bi-search" />
          <input type="text" placeholder="Search games..." value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
      </div>

      <div className={styles.body}>
        {isSearching ? (
          <div className={styles.section}>
            {searching && <p className={styles.emptyState}>Searching...</p>}
            {!searching && searchError && <p className={styles.emptyState}>Something went wrong searching games.</p>}
            {!searching && !searchError && !searchResults.length && (
              <p className={styles.emptyState}>No games match &quot;{query}&quot;.</p>
            )}
            <div className={styles.grid}>
              {searchResults.map((game) => (
                <GameCard key={game.subdomain} game={game} onClick={(g) => setActiveSubdomain(g.subdomain)} />
              ))}
            </div>
            {searchCursor && (
              <button className={styles.loadMore} onClick={loadMoreSearchResults} disabled={searchLoadingMore}>
                {searchLoadingMore ? 'Loading...' : 'Load more'}
              </button>
            )}
          </div>
        ) : (
          <>
            {loading && <p className={styles.emptyState}>Loading games...</p>}
            {!loading && loadError && <p className={styles.emptyState}>Something went wrong loading games.</p>}
            {!loading && !loadError && !sections.length && (
              <p className={styles.emptyState}>No games have been added to the showcase yet.</p>
            )}
            {sections.map((row) => (
              <GameRow
                key={row.linkTo}
                title={row.title}
                linkTo={row.linkTo}
                games={row.games}
                onSelectGame={(game) => setActiveSubdomain(game.subdomain)}
              />
            ))}
          </>
        )}
      </div>

      <GameModal subdomain={activeSubdomain} show={!!activeSubdomain} onHide={() => setActiveSubdomain(null)} />

      <Footer />
    </div>
  )
}

export default GamesDirectory
